import React, { useState } from 'react';
import { Info } from 'lucide-react';
import type { SDQDimensionScore } from '../../../types';
import { DIMENSION_LABELS, DIMENSION_DESCRIPTIONS, scoreColor, gradeColor } from './dashboard-utils';

interface DimensionBarProps {
  name: string;
  dim: SDQDimensionScore;
}

export default function DimensionBar({ name, dim }: DimensionBarProps) {
  // Descriptions expanded by DEFAULT (the (i) button still toggles
  // them closed) — users wanted them visible without clicking.
  const [showDesc, setShowDesc] = useState(true);
  const label = DIMENSION_LABELS[name] || name;
  const description = DIMENSION_DESCRIPTIONS[name];
  const color = scoreColor(dim.score);
  const gColor = gradeColor(dim.grade);
  const issueCount = dim.issues.length;

  // Extract cargo vs ge breakdown if available
  const cargoScore = dim.sub_scores?.cargo_raw_score;
  const geScore = dim.sub_scores?.ge_raw_score;

  return (
    <div>
      <div className="flex items-center gap-3 py-2">
        {/* Label + info toggle */}
        <div className="w-36 shrink-0 flex items-center gap-1.5">
          <span className="text-sm text-cargo-text font-medium truncate">{label}</span>
          {description && (
            <button
              onClick={() => setShowDesc((v) => !v)}
              className={`shrink-0 p-0.5 rounded transition-colors ${
                showDesc ? 'text-cargo-accent' : 'text-cargo-muted/50 hover:text-cargo-muted'
              }`}
              title={`What is ${label}?`}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Bar track */}
        <div className="flex-1 min-w-0 relative">
          <div className="h-5 bg-cargo-panel/60 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(dim.score, 2)}%`, backgroundColor: color }}
            />
          </div>
          {/* Breakdown labels */}
          {cargoScore != null && geScore != null && (
            <div className="flex gap-3 mt-0.5 text-[10px] text-cargo-muted">
              <span>CARGO: {cargoScore}</span>
              <span>GE: {geScore}</span>
            </div>
          )}
        </div>

        {/* Score */}
        <div className="w-12 shrink-0 text-right font-semibold text-sm tabular-nums" style={{ color }}>
          {dim.score.toFixed(1)}
        </div>

        {/* Grade badge */}
        <span
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: gColor }}
        >
          {dim.grade}
        </span>

        {/* Issue count */}
        <span className="w-16 shrink-0 text-xs text-cargo-muted text-right">
          {issueCount > 0 ? `${issueCount} issue${issueCount !== 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {/* Description row */}
      {showDesc && description && (
        <div className="ml-[9.5rem] mr-24 mb-2 px-3 py-2 bg-cargo-accent/5 border border-cargo-accent/15 rounded-lg text-xs text-cargo-muted leading-relaxed">
          {description}
        </div>
      )}
    </div>
  );
}
